/**
 * DEFCAT P6 — Overview tab: catalogue metadata (name / code / visibility),
 * edit + save via PATCH. Scope is immutable (display-only).
 */
import { useEffect, useState, type ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import type {
  CatalogueVisibility,
  DefectCatalogueAdmin,
  UpdateCatalogueBody,
} from "@aeriosiq/types";
import { useUpdateCatalogueMutation } from "./taxonomyQueries.js";
import { locText } from "./locText.js";

export interface OverviewTabProps {
  catalogue: DefectCatalogueAdmin;
  /** Role-gated: when false the metadata form is read-only (no Save). */
  canEdit: boolean;
}

export function OverviewTab({
  catalogue,
  canEdit,
}: OverviewTabProps): ReactElement {
  const [name, setName] = useState(locText(catalogue.name, ""));
  const [code, setCode] = useState(catalogue.tenantCatalogueCode);
  const [visibility, setVisibility] = useState<CatalogueVisibility>(
    catalogue.visibility,
  );
  const update = useUpdateCatalogueMutation(catalogue.catalogueId);

  useEffect(() => {
    setName(locText(catalogue.name, ""));
    setCode(catalogue.tenantCatalogueCode);
    setVisibility(catalogue.visibility);
  }, [catalogue]);

  function handleSave(): void {
    const body: UpdateCatalogueBody = {
      name: { en: name.trim() },
      tenantCatalogueCode: code,
      visibility,
    };
    update.mutate(body);
  }

  return (
    <Box sx={{ maxWidth: 520, p: 1 }}>
      <Stack spacing={2.5}>
        {update.isError ? (
          <Alert severity="error">Could not save metadata.</Alert>
        ) : null}
        {update.isSuccess ? <Alert severity="success">Saved.</Alert> : null}
        <TextField
          label="Name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
          }}
          fullWidth
          disabled={!canEdit}
        />
        <TextField
          label="Catalogue code"
          value={code}
          onChange={(e) => {
            setCode(e.target.value);
          }}
          fullWidth
          disabled={!canEdit}
        />
        <TextField
          select
          label="Visibility"
          value={visibility}
          onChange={(e) => {
            setVisibility(e.target.value as CatalogueVisibility);
          }}
          fullWidth
          disabled={!canEdit}
        >
          <MenuItem value="private">Private</MenuItem>
          <MenuItem value="shared">Shared</MenuItem>
        </TextField>
        <Typography variant="body2" color="text.secondary">
          Scope: <strong>{catalogue.scope}</strong> (immutable after create)
        </Typography>
        {canEdit ? (
          <Box>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={update.isPending || name.trim().length === 0}
            >
              Save
            </Button>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            You don&apos;t have permission to edit this catalogue&apos;s
            metadata.
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
